/**
 * Wiring shared by all three apps: one ApiClient, one QueryClient, one session.
 *
 * Each app mounts this once and then only ever calls hooks. The apps differ in
 * what they render, not in how they talk to the API — which is what keeps the
 * contract in one place when a fourth surface shows up.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  ApiClient,
  ApiError,
  UnauthenticatedError,
  createTokenStore,
  tokensFrom,
  type SessionTokens,
} from './client';

const ApiContext = createContext<ApiClient | null>(null);
const SessionContext = createContext<Session | null>(null);

/**
 * Moving the till into practice and back (`practice.ts`).
 *
 * Practice is a session of its own, with its own tokens. The till's real
 * sign-in is kept aside while it practises, under the same storage key with
 * `.real` after it, and put back when practice ends, or when the practice
 * tokens stop renewing because practice ran out. A till never has to sign in
 * again because it practised.
 */
export interface PracticeSwitch {
  enter: (tokens: SessionTokens) => void;
  leave: () => void;
  /** The practice session's refresh token, to give up when it ends. */
  practiceRefresh: () => string | null;
}

const PracticeContext = createContext<PracticeSwitch | null>(null);

export interface MeResponse {
  user_id: string;
  tenant_id: string;
  /** Null for somebody who only works a till and signs in by PIN. */
  email: string | null;
  name: string | null;
  permissions: string[];
}

export interface LoginRequest {
  tenant_slug: string;
  email: string;
  password: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  /** Fifteen minutes. Renewed behind the app's back; nothing renders this. */
  expires_in: number;
  user_id: string;
  tenant_id: string;
  /** Spent on the next renewal and replaced. Thirty days. */
  refresh_token: string;
  refresh_expires_in: number;
}

export interface Session {
  isAuthenticated: boolean;
  /**
   * Ends it here and at the server, which revokes every device descended from
   * the same sign-in. Fire and forget: the local session is gone the moment
   * this is called, whatever the network does.
   */
  signOut: () => void;
  /** Bumped on sign-in/out so dependent queries refetch rather than go stale. */
  epoch: number;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Retrying a 401 or a 402 just delays the honest answer. Retry the
        // things that are actually transient.
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            return false;
          }
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
    },
  });
}

export function ApiProvider({
  baseUrl,
  storageKey,
  storage = 'local',
  children,
}: {
  baseUrl: string;
  storageKey?: string;
  /** `session`: this tab only, gone with the tab (see `createTokenStore`). */
  storage?: 'local' | 'session';
  children: ReactNode;
}) {
  const [queryClient] = useState(createQueryClient);
  const [epoch, setEpoch] = useState(0);

  const tokens = useMemo(() => createTokenStore(storageKey, storage), [storageKey, storage]);
  // The real sign-in, while the till practises (`PracticeSwitch`).
  const aside = useMemo(
    () => createTokenStore(`${storageKey ?? 'restaurant.session'}.real`, storage),
    [storageKey, storage],
  );
  const [authed, setAuthed] = useState(() => tokens.get() !== null);

  const client = useMemo(
    () =>
      new ApiClient({
        baseUrl,
        tokens,
        // Reached only once renewal has failed or there is nothing left to
        // renew with — the client handles an expired access token itself. A
        // session that is genuinely gone must not leave the app rendering a
        // shell it can no longer fill.
        onUnauthenticated: () => {
          // Practice that ran out, not a sign-in that ended: back to the
          // real one kept aside.
          const real = aside.get();
          aside.set(null);
          tokens.set(real);
          setAuthed(real !== null);
          setEpoch((n) => n + 1);
          queryClient.removeQueries();
        },
      }),
    [baseUrl, tokens, aside, queryClient],
  );

  const signOut = useCallback(() => {
    // Clears the tokens itself, then tells the server in its own time.
    void client.signOut();
    // Signing out of a practising till signs the till out, not into the
    // sign-in it kept aside.
    aside.set(null);
    setAuthed(false);
    setEpoch((n) => n + 1);
    // Queries only, for the same reason as above.
    queryClient.removeQueries();
  }, [client, aside, queryClient]);

  const practice = useMemo<PracticeSwitch>(
    () => ({
      enter: (next) => {
        if (aside.get() === null) aside.set(tokens.get());
        tokens.set(next);
        setAuthed(true);
        setEpoch((n) => n + 1);
        queryClient.removeQueries();
      },
      leave: () => {
        const real = aside.get();
        aside.set(null);
        tokens.set(real);
        setAuthed(real !== null);
        setEpoch((n) => n + 1);
        queryClient.removeQueries();
      },
      practiceRefresh: () => (aside.get() === null ? null : (tokens.get()?.refresh ?? null)),
    }),
    [aside, tokens, queryClient],
  );

  const session = useMemo<Session>(
    () => ({ isAuthenticated: authed && tokens.get() !== null, signOut, epoch }),
    [authed, tokens, signOut, epoch],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <ApiContext.Provider value={client}>
        <SessionContext.Provider value={session}>
          <PracticeContext.Provider value={practice}>
            <SignInSetter onSignedIn={() => setAuthed(true)}>{children}</SignInSetter>
          </PracticeContext.Provider>
        </SessionContext.Provider>
      </ApiContext.Provider>
    </QueryClientProvider>
  );
}

const SignInCallbackContext = createContext<(() => void) | null>(null);

function SignInSetter({
  onSignedIn,
  children,
}: {
  onSignedIn: () => void;
  children: ReactNode;
}) {
  return (
    <SignInCallbackContext.Provider value={onSignedIn}>
      {children}
    </SignInCallbackContext.Provider>
  );
}

export function useApi(): ApiClient {
  const client = useContext(ApiContext);
  if (client === null) throw new Error('useApi must be used inside an <ApiProvider>');
  return client;
}

export function usePracticeSwitch(): PracticeSwitch {
  const practice = useContext(PracticeContext);
  if (practice === null)
    throw new Error('usePracticeSwitch must be used inside an <ApiProvider>');
  return practice;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) throw new Error('useSession must be used inside an <ApiProvider>');
  return session;
}

/**
 * What happens once there is a token, however it was got.
 *
 * Shared by sign-in and the second factor rather than copied, because the order of these
 * lines is a bug fix and a second copy would be free to undo it.
 */
function signedIn(
  api: ApiClient,
  queryClient: ReturnType<typeof useQueryClient>,
  onSignedIn: (() => void) | null | undefined,
  result: TokenResponse,
): void {
  api.tokens.set(tokensFrom(result));

  // Signal before touching the cache, and remove queries rather than
  // clearing everything. `queryClient.clear()` empties the mutation cache
  // too — including this mutation, mid-callback — so anything after it in
  // this function silently never runs. The symptom was a successful login
  // that stored a token and then sat on the sign-in screen until reload.
  onSignedIn?.();
  queryClient.removeQueries();
}

/**
 * Store a session got some other way than the staff sign-in, and let the app
 * know it is signed in.
 *
 * For the supplier portal, whose token is not a staff token and comes from its
 * own sign-in and invitation routes. Same order of steps as `signedIn`, for
 * the same reason.
 */
export function useStartSession(): (tokens: SessionTokens) => void {
  const api = useApi();
  const queryClient = useQueryClient();
  const onSignedIn = useContext(SignInCallbackContext);
  return useCallback(
    (tokens: SessionTokens) => {
      api.tokens.set(tokens);
      onSignedIn?.();
      queryClient.removeQueries();
    },
    [api, queryClient, onSignedIn],
  );
}

/**
 * A sign-in that stopped half way, because the account has a second factor.
 *
 * Deliberately carries no tokens: the challenge proves a password was right
 * and nothing else, and the server refuses it anywhere an access or refresh
 * token is expected. It is only good for `/v1/auth/mfa`, once.
 */
export interface MfaChallenge {
  mfa_required: true;
  challenge_token: string;
  /** Seconds. Long enough to unlock a phone, short enough to matter. */
  expires_in: number;
}

export type LoginOutcome = TokenResponse | MfaChallenge;

/** Whether a sign-in finished or still wants six digits. */
export function needsMfa(outcome: LoginOutcome): outcome is MfaChallenge {
  return 'mfa_required' in outcome;
}

/**
 * Sign in with a password.
 *
 * May answer with an `MfaChallenge` instead of tokens — check `needsMfa`
 * before reading either. Nothing is stored in that case, because a
 * half-finished sign-in is not a session.
 */
export function useLogin() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onSignedIn = useContext(SignInCallbackContext);

  return useMutation<LoginOutcome, ApiError, LoginRequest>({
    mutationFn: (credentials) => api.post<LoginOutcome>('/v1/auth/login', credentials),
    onSuccess: (result) => {
      if (needsMfa(result)) return;
      signedIn(api, queryClient, onSignedIn, result);
    },
  });
}

/**
 * Finish a sign-in with a code from the authenticator app, or a recovery code.
 *
 * Everything refuses alike (401): an expired challenge, a wrong code, and an
 * account that turned its second factor off in between. The screen's job is to
 * say "that code is not right" and let them try again — a challenge that has
 * expired needs the password screen, which is what `expires_in` is for.
 */
export function useCompleteMfa() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onSignedIn = useContext(SignInCallbackContext);

  return useMutation<TokenResponse, ApiError, { challengeToken: string; code: string }>({
    mutationFn: ({ challengeToken, code }) =>
      api.post<TokenResponse>('/v1/auth/mfa', {
        challenge_token: challengeToken,
        code,
      }),
    onSuccess: (result) => signedIn(api, queryClient, onSignedIn, result),
  });
}

/**
 * Swap who is working this terminal, on four digits.
 *
 * Needs an existing session, which is what makes four digits acceptable: the
 * till is signed in once at open by somebody with a password, and staff swap
 * on it through the shift. A public route taking a PIN would be guessed from
 * anywhere on the internet.
 *
 * The session it stores carries the *PIN holder's* permissions, not the
 * caller's — so everything cached for the previous person is dropped, exactly
 * as at a password sign-in. Getting that wrong would show one member of staff
 * another's screen.
 *
 * A PIN nobody has and a PIN belonging to somebody with no login are refused
 * identically (401): the difference would be a way to find out which numbers
 * are real. Too much guessing answers 429.
 */
export function usePinSignIn() {
  const api = useApi();
  const queryClient = useQueryClient();
  const onSignedIn = useContext(SignInCallbackContext);

  return useMutation<TokenResponse, ApiError, { pin: string }>({
    // A wrong PIN is 401, and that is an answer about four digits, not about
    // the till's session. Without this the client renewed, was refused again,
    // and signed the whole terminal out � one mistyped PIN and the next person
    // needed the manager's password to get the till back.
    mutationFn: ({ pin }) =>
      api.post<TokenResponse>(
        '/v1/staff/pin-sign-in',
        { pin },
        { unauthenticatedEndsSession: false },
      ),
    onSuccess: (result) => signedIn(api, queryClient, onSignedIn, result),
  });
}

export function useMe() {
  const api = useApi();
  const session = useSession();
  return useQuery({
    queryKey: ['me', session.epoch],
    queryFn: ({ signal }) => api.get<MeResponse>('/v1/me', signal),
    enabled: session.isAuthenticated,
  });
}

export function usePermission(permission: string): boolean {
  const { data } = useMe();
  if (!data) return false;
  return data.permissions.includes(permission) || data.permissions.includes('*');
}

export { UnauthenticatedError };
